/**
 * Admin ↔ User messaging for biz.omore.app.
 *
 * Firestore structure:
 *   bizMessages/{userId}                  — one conversation doc per user
 *   bizMessages/{userId}/thread/{msgId}   — individual messages
 *
 * To grant admin access create a document at adminUsers/{uid}.
 */
import {
  collection,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  setDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  type Unsubscribe,
  type Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';

const BIZ_MESSAGES = 'bizMessages';

export interface Conversation {
  id: string;
  userId: string;
  explorerName: string;
  lastMessage: string;
  lastMessageAt: Timestamp | null;
  unreadByAdmin: number;
  unreadByUser: number;
  status: 'open' | 'resolved';
}

export interface ThreadMessage {
  id: string;
  content: string;
  fromAdmin: boolean;
  senderName: string;
  createdAt: Timestamp | null;
  read: boolean;
}

// ── Admin-side ────────────────────────────────────────────────────────────────

export function subscribeToConversations(
  cb: (convs: Conversation[]) => void,
): Unsubscribe {
  const q = query(
    collection(db, BIZ_MESSAGES),
    orderBy('lastMessageAt', 'desc'),
    limit(100),
  );
  return onSnapshot(q, (snap) => {
    cb(snap.docs.map((d) => ({ id: d.id, ...d.data() } as Conversation)));
  });
}

export function subscribeToThread(
  userId: string,
  cb: (msgs: ThreadMessage[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, BIZ_MESSAGES, userId, 'thread'),
    orderBy('createdAt', 'asc'),
  );
  return onSnapshot(
    q,
    (snap) => {
      cb(snap.docs.map((d) => ({ id: d.id, ...d.data() } as ThreadMessage)));
    },
    (err) => {
      if (onError) onError(err);
      else console.warn('subscribeToThread error:', err.code);
    },
  );
}

export async function sendAdminReply(
  userId: string,
  content: string,
  adminName = 'omore Team',
) {
  const convRef = doc(db, BIZ_MESSAGES, userId);
  const convSnap = await getDoc(convRef);

  await addDoc(collection(db, BIZ_MESSAGES, userId, 'thread'), {
    content,
    fromAdmin: true,
    senderName: adminName,
    createdAt: serverTimestamp(),
    read: false,
  });

  await setDoc(
    convRef,
    {
      userId,
      explorerName: convSnap.exists() ? convSnap.data().explorerName : 'Unknown',
      lastMessage: content,
      lastMessageAt: serverTimestamp(),
      unreadByUser: convSnap.exists()
        ? (convSnap.data().unreadByUser ?? 0) + 1
        : 1,
      unreadByAdmin: 0,
      status: 'open',
    },
    { merge: true },
  );
}

export async function markAdminRead(userId: string) {
  try {
    await updateDoc(doc(db, BIZ_MESSAGES, userId), { unreadByAdmin: 0 });
  } catch {
    // doc may not exist yet — safe to ignore
  }
}

export async function resolveConversation(userId: string) {
  await updateDoc(doc(db, BIZ_MESSAGES, userId), { status: 'resolved' });
}

// ── User-side ─────────────────────────────────────────────────────────────────

export async function sendUserMessage(
  userId: string,
  explorerName: string,
  content: string,
) {
  const convRef = doc(db, BIZ_MESSAGES, userId);
  const convSnap = await getDoc(convRef);

  await addDoc(collection(db, BIZ_MESSAGES, userId, 'thread'), {
    content,
    fromAdmin: false,
    senderName: explorerName,
    createdAt: serverTimestamp(),
    read: false,
  });

  await setDoc(
    convRef,
    {
      userId,
      explorerName,
      lastMessage: content,
      lastMessageAt: serverTimestamp(),
      unreadByAdmin: convSnap.exists()
        ? (convSnap.data().unreadByAdmin ?? 0) + 1
        : 1,
      unreadByUser: 0,
      status: 'open',
    },
    { merge: true },
  );
}

export function subscribeToUserThread(
  userId: string,
  cb: (msgs: ThreadMessage[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  return subscribeToThread(userId, cb, onError);
}

export async function markUserRead(userId: string) {
  try {
    await updateDoc(doc(db, BIZ_MESSAGES, userId), { unreadByUser: 0 });
  } catch {
    // doc may not exist yet — safe to ignore
  }
}
